import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { toast } from "sonner";
import { RefreshCw, Search, ArrowLeft, Trash2, Copy, Plus, FileText } from "lucide-react";
import OfferteDetail from "./OfferteDetail";
import NewQuoteDialog from "./NewQuoteDialog";
import { AdminPageHeader } from "../ui";

export const QUOTE_STATUSES = ["nieuw", "in_behandeling", "wacht_klantinfo", "wacht_documenten", "wacht_betaling", "goedgekeurd", "afgewezen", "verlopen", "omgezet"];
export const QUOTE_STATUS_LABELS = {
  nieuw: "Nieuw", new: "Nieuw", in_behandeling: "In behandeling",
  wacht_klantinfo: "Wacht klantinfo", wacht_documenten: "Wacht documenten",
  wacht_betaling: "Wacht betaling", goedgekeurd: "Goedgekeurd",
  afgewezen: "Afgewezen", verlopen: "Verlopen", omgezet: "Omgezet naar opdracht",
};

const STATUS_COLORS = {
  nieuw: "bg-blue-100 text-blue-700 border-blue-300",
  new: "bg-blue-100 text-blue-700 border-blue-300",
  in_behandeling: "bg-yellow-100 text-yellow-800 border-yellow-300",
  wacht_klantinfo: "bg-purple-100 text-purple-700 border-purple-300",
  wacht_documenten: "bg-orange-100 text-orange-800 border-orange-300",
  wacht_betaling: "bg-red-100 text-red-700 border-red-300",
  goedgekeurd: "bg-green-100 text-green-700 border-green-300",
  afgewezen: "bg-gray-200 text-gray-700 border-gray-300",
  verlopen: "bg-gray-400 text-white border-gray-500",
  omgezet: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${STATUS_COLORS[status] || "bg-gray-100 text-gray-700"}`}>
      {QUOTE_STATUS_LABELS[status] || status}
    </span>
  );
}

export default function OffertesSection({ initialQuoteId = null } = {}) {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [selected, setSelected] = useState(initialQuoteId || null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const canEdit = hasPermission("quotes:edit");
  const canCreate = hasPermission("quotes:create") || canEdit || isAdminOrOwner();
  const canDelete = isAdminOrOwner();

  // Iteratie A — bij deep-link vanuit entity search: selecteer offerte.
  // Query-param wordt BEHOUDEN zodat F5 het detail opnieuw opent.
  useEffect(() => {
    if (initialQuoteId) {
      setSelected(initialQuoteId);
    }
  }, [initialQuoteId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusF !== "all") params.status = statusF;
      const r = await adminApi.get("/admin/quotes-v2", { params });
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, statusF]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Offerte verwijderen?")) return;
    try {
      await adminApi.delete(`/admin/quotes-v2/${id}`);
      toast.success("Offerte verwijderd");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDuplicate = async (id) => {
    try {
      const r = await adminApi.post(`/admin/quotes-v2/${id}/duplicate`);
      toast.success("Offerte gedupliceerd");
      setSelected(r.data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onChangeStatus = async (id, status) => {
    try {
      await adminApi.put(`/admin/quotes-v2/${id}/status`, { status });
      load();
      toast.success("Status bijgewerkt");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (selected) {
    return (
      <div data-testid="admin-section-offertes-detail">
        <button onClick={() => { setSelected(null); load(); }} className="inline-flex items-center text-sm text-[#0055FF] hover:underline mb-4" data-testid="offerte-back-button">
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug naar offerteslijst
        </button>
        <OfferteDetail quoteId={selected} onChanged={load} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-section-offertes">
      <AdminPageHeader
        icon={FileText}
        title="Offertes"
        subtitle="Beheer offerteaanvragen, statussen en notities"
        actions={
          <>
            {canCreate && (
              <Button
                onClick={() => setNewDialogOpen(true)}
                size="sm"
                className="rounded-full bg-brand-500 hover:bg-brand-700 text-white shadow-brand-soft"
                data-testid="offertes-new-quote-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Nieuwe offerte maken
              </Button>
            )}
            <Button onClick={load} variant="outline" size="sm" className="rounded-full border-slate-200 hover:border-brand-500 hover:text-brand-500" data-testid="offertes-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Vernieuwen
            </Button>
          </>
        }
      />

      <NewQuoteDialog
        open={newDialogOpen}
        onClose={() => setNewDialogOpen(false)}
        onCreated={(id) => { load(); if (id) setSelected(id); }}
      />

      <div className="mt-6 flex flex-wrap items-center gap-2 bg-white border border-gray-200 p-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="offertes-search" placeholder="Zoek op naam, e-mail, telefoon..." value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="offertes-filter-status" className="rounded-none w-52"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s}>{QUOTE_STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 bg-white border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-gray-500">Laden...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Geen offertes gevonden.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] border-b border-gray-200 text-left">
              <tr>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Datum</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Naam / E-mail</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Service</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Bestemming</th>
                <th className="p-3 font-chivo font-bold text-xs uppercase tracking-wide">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((qr) => (
                <tr key={qr.id} data-testid={`offerte-row-${qr.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-3 text-xs whitespace-nowrap cursor-pointer" onClick={() => setSelected(qr.id)}>{qr.created_at?.slice(0, 10)}</td>
                  <td className="p-3 cursor-pointer" onClick={() => setSelected(qr.id)}>
                    <div className="font-medium text-[#0A192F]">{qr.name || [qr.first_name, qr.last_name].filter(Boolean).join(" ") || "—"}</div>
                    <div className="text-xs text-gray-500">{qr.email}</div>
                  </td>
                  <td className="p-3 text-xs cursor-pointer" onClick={() => setSelected(qr.id)}>{qr.service || qr.shipment?.method || "—"}</td>
                  <td className="p-3 text-xs cursor-pointer" onClick={() => setSelected(qr.id)}>{qr.destination_country || qr.destination || "Curaçao"}</td>
                  <td className="p-3">
                    {canEdit ? (
                      <Select value={QUOTE_STATUSES.includes(qr.status) ? qr.status : "nieuw"} onValueChange={(v) => onChangeStatus(qr.id, v)}>
                        <SelectTrigger className="w-44 rounded-none h-8 text-xs" data-testid={`offerte-status-${qr.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s}>{QUOTE_STATUS_LABELS[s]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <StatusBadge status={qr.status} />
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && (
                        <button onClick={() => onDuplicate(qr.id)} className="p-1.5 text-gray-400 hover:text-[#0055FF]" title="Dupliceer" data-testid={`offerte-duplicate-${qr.id}`}>
                          <Copy className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => onDelete(qr.id)} className="p-1.5 text-gray-400 hover:text-red-500" title="Verwijderen" data-testid={`offerte-delete-${qr.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
